import { getProfileDefaultView } from "../config/profile-view";
import { resolveWakeLoopProfileOrNull } from "../config/resolve-wakeloop-profile";
import {
  getConfiguredViewCustomization,
  type RuntimeViewCustomization,
} from "../config/runtime-config";
import { resolveViewProfileKey } from "../config/view-profile-key";
import type { CliViewMode, RuntimeContext } from "./types";

export type { CliViewMode, RuntimeContext } from "./types";

export async function resolveRuntimeContext(input?: {
  view?: string;
  profile?: string;
  interactive?: boolean;
}): Promise<RuntimeContext> {
  const viewWakeLoopProfileId = await resolveViewProfileKey(input?.profile);
  const configuredViewMode = await readConfiguredDefaultView(
    viewWakeLoopProfileId
  );
  const viewMode = parseCliViewMode(input?.view ?? configuredViewMode);
  const interactive = input?.interactive ?? isInteractiveSession();
  const viewCustomization = await readViewCustomization();

  if (viewMode === "human" || viewMode === "agent") {
    return {
      viewMode,
      resolvedView: viewMode,
      interactive,
      ...(viewWakeLoopProfileId
        ? { wakeLoopProfileId: viewWakeLoopProfileId }
        : {}),
      ...(viewCustomization ? { viewCustomization } : {}),
    };
  }

  const wakeLoopProfile = await resolveWakeLoopProfileOrNull({
    wakeLoopProfileId: input?.profile,
    allowUnauthenticated: true,
    persistSelection: false,
    allowDefaultFallback: false,
  }).catch(() => null);
  const resolvedView =
    wakeLoopProfile?.profileKind === "agent" ? "agent" : "human";
  const resolvedWakeLoopProfileId = wakeLoopProfile
    ? wakeLoopProfile.wakeLoopProfileId
    : viewWakeLoopProfileId;

  return {
    viewMode,
    resolvedView,
    interactive,
    ...(resolvedWakeLoopProfileId
      ? { wakeLoopProfileId: resolvedWakeLoopProfileId }
      : {}),
    ...(wakeLoopProfile?.profileKind
      ? { profileKind: wakeLoopProfile.profileKind }
      : {}),
    ...(viewCustomization ? { viewCustomization } : {}),
  };
}

export function parseCliViewMode(value?: string): CliViewMode {
  const normalized = String(value ?? "auto")
    .trim()
    .toLowerCase();
  if (
    normalized === "auto" ||
    normalized === "human" ||
    normalized === "agent"
  ) {
    return normalized;
  }

  throw new Error(`invalid --view value: ${normalized} (use auto|human|agent)`);
}

function readViewCustomization(): Promise<RuntimeViewCustomization | null> {
  return getConfiguredViewCustomization().catch(() => null);
}

function readConfiguredDefaultView(
  wakeLoopProfileId?: string | null
): Promise<CliViewMode> {
  if (!wakeLoopProfileId) {
    return Promise.resolve("auto");
  }
  return getProfileDefaultView(wakeLoopProfileId).catch(() => "auto");
}

function isInteractiveSession(): boolean {
  return Boolean(process.stdin.isTTY && process.stdout.isTTY);
}
